import { type Moment } from "moment";
import { useCallback, useContext } from "react";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";

/**
 * Custom hook that provides date filter utilities with builder context injected.
 *
 * BuilderInfoContext is read optionally rather than required: BTAdmin / no-GlobalUser sessions reuse the
 * standard FilterDrawer but have no builder context, and the week-start-aware disabling below is only
 * relevant to builder-facing week/semi-month date pickers (BTAdmin filters use plain date ranges). When the
 * context is absent, week disabling is simply skipped instead of crashing the whole filter.
 */
export const useBuilderDateFilterUtils = () => {
    const builderInfo = useContext(BuilderInfoContext);
    const weekStartDay = builderInfo?.weekStartDay;

    /**
     * Checks if a date should be disabled for weekly date selection.
     * Only the first day of the week based on the builder's week start day should be enabled.
     * @param currentDate The date to check
     * @returns true if the date should be disabled, false otherwise
     */
    const isWeekDateDisabled = useCallback(
        (currentDate: Moment | null) => {
            if (currentDate !== null && weekStartDay != null) {
                const dayOfWeek = currentDate.weekday(); // 0 - 6; 0 = Sunday, 6 = Saturday
                // weekStartDay is a bitmask: 0 - 127; 0 = None; 1 = Sunday; 64 = Saturday; 127 = All; (DaysOfWeek Enum)
                return !(weekStartDay & (1 << dayOfWeek));
            }
            return false;
        },
        [weekStartDay],
    );

    /**
     * Checks if a date should be disabled for semi-monthly date selection.
     * Only the 1st and 15th of each month should be enabled.
     * @param currentDate The date to check
     * @returns true if the date should be disabled, false otherwise
     */
    const isSemiMonthDateDisabled = useCallback((currentDate: Moment | null) => {
        return !["1", "15"].includes(currentDate?.format("D") ?? "");
    }, []);

    /**
     * Checks if a date should be disabled for monthly date selection.
     * Only the 1st of each month should be enabled.
     * @param currentDate The date to check
     * @returns true if the date should be disabled, false otherwise
     */
    const isMonthDateDisabled = useCallback((currentDate: Moment | null) => {
        return currentDate?.format("D") !== "1";
    }, []);

    return {
        isWeekDateDisabled,
        isSemiMonthDateDisabled,
        isMonthDateDisabled,
    };
};
